import React, { useEffect, useRef } from 'react';
import { ShipType, ShipCategory, ShipStatus, ShipLocation } from '../../types/ship-types';
import type { ShipDto } from '../../types/ship-types';
import { MiningZoneConstants } from '../../constants/miningZones';
import mothershipImg from '../../assets/ships/mothership.png';
// Import all asteroid belt images
import oreBeltImg from '../../assets/belts/ore-belt.png';
import iceBeltImg from '../../assets/belts/ice-belt.png';
import lavaBeltImg from '../../assets/belts/lava-belt.png';
import miningFrigateImg from '../../assets/ships/mining-frigate.png';
import commandShipImg from '../../assets/ships/command-ship.png';
import haulerShipImg from '../../assets/ships/hauler-ship.png';
import freighterImg from '../../assets/ships/freighter.png';
import bargeImg from '../../assets/ships/barge.png';
import pirateImg from '../../assets/ships/pirate.png';
import { debugLog } from '../../utils/debugLogger';

interface FleetVisualizationProps {
  fleet: ShipDto[];
  currentMiningZoneId?: string; // ID of the current mining zone
  pirateAttackActive?: boolean;
}

// Belt image mapping helper
const getBeltImage = (zoneId: string | undefined | null): string => {
  // Handle undefined or null by defaulting to ore belt
  if (!zoneId) {
    debugLog('No mining zone ID provided, using default ore belt');
    return oreBeltImg;
  }
  
  // Normalize zone ID by removing spaces and converting to lowercase
  const normalizedZoneId = zoneId.replace(/\s+/g, '').toLowerCase();
  
  debugLog('Normalizing zone ID', { original: zoneId, normalized: normalizedZoneId });
  
  // Use a cached result if we've processed this normalized ID before
  // to ensure consistency between calls
  let selectedBelt: string;
  
  // Simple and explicit mapping for common zone IDs
  switch (normalizedZoneId) {
    case MiningZoneConstants.ORE_BELT.toLowerCase():
      selectedBelt = oreBeltImg;
      break;
    case MiningZoneConstants.ICE_BELT.toLowerCase():
      selectedBelt = iceBeltImg;
      break;
    case MiningZoneConstants.LAVA_BELT.toLowerCase():
      selectedBelt = lavaBeltImg;
      break;
    default:
      // Additional fallback logic for partial matches
      if (normalizedZoneId.includes('ore')) {
        selectedBelt = oreBeltImg;
      } else if (normalizedZoneId.includes('ice')) {
        selectedBelt = iceBeltImg;
      } else if (normalizedZoneId.includes('lava')) {
        selectedBelt = lavaBeltImg;
      } else {
        // Default to ore belt for unrecognized zones
        selectedBelt = oreBeltImg;
        debugLog('Using default ore belt for unknown zone:', zoneId);
      }
      break;
  }
  
  debugLog('Selected belt image', { 
    zoneId, 
    normalizedZoneId, 
    selectedBelt: selectedBelt === oreBeltImg ? 'ore' : 
                 selectedBelt === iceBeltImg ? 'ice' : 
                 selectedBelt === lavaBeltImg ? 'lava' : 'unknown'
  });
  
  return selectedBelt;
};

interface ShipAnimationState {
  pos: number;             // Current position (0=mothership, 1=asteroids)
  targetPos: number;       // Target position to animate to
  startPos: number;        // Starting position of current animation
  startTime: number;       // Animation start timestamp
  duration: number;        // Total animation duration in ms
  status: string;          // Current ship status
}

// Include all ship types we want to show in the visualization
// Ship types that should be visualized are handled directly in the filtering logic

// Add these constants for edge margins
const MOTHERSHIP_POS = -0.08; // Move closer to the left edge
// ASTEROID_POS will be dynamic based on fullscreen

const FleetVisualization: React.FC<FleetVisualizationProps> = ({ fleet, currentMiningZoneId = 'ore belt', pirateAttackActive = false }) => {
  // Log the incoming mining zone ID to help debug
  debugLog('FleetVisualization received mining zone:', currentMiningZoneId);
  
  const shipsVisualRef = useRef<HTMLDivElement>(null);
  const starsCanvasRef = useRef<HTMLCanvasElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(1200);
  const [isFullscreen] = React.useState(false);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  
  // Get the initial belt image once on component mount
  const initialBeltImage = React.useMemo(() => getBeltImage(currentMiningZoneId), []);
  
  // Track current and previous belt images for transitions
  const [currentBelt, setCurrentBelt] = React.useState(initialBeltImage);
  const [prevBelt, setPrevBelt] = React.useState(initialBeltImage);
  const [isBeltTransitioning, setIsBeltTransitioning] = React.useState(false);
  const [showPirateAlert, setShowPirateAlert] = React.useState(false);

  // Reference to keep track of the current mining zone to prevent redundant updates
  const lastZoneRef = React.useRef<string | undefined | null>();

  useEffect(() => {
    if (pirateAttackActive) {
      setShowPirateAlert(true);
      const t = setTimeout(() => setShowPirateAlert(false), 1000);
      return () => clearTimeout(t);
    }
  }, [pirateAttackActive]);
  
  // Single effect to handle mining zone changes and transitions
  useEffect(() => {
    debugLog('Mining zone effect triggered', { 
      currentMiningZoneId,
      lastZone: lastZoneRef.current,
      hasChanged: currentMiningZoneId !== lastZoneRef.current
    });
    
    // Skip if the mining zone hasn't actually changed
    if (currentMiningZoneId === lastZoneRef.current && lastZoneRef.current !== undefined) {
      debugLog('Mining zone unchanged, skipping update');
      return;
    }
    
    // Update the reference to track changes
    lastZoneRef.current = currentMiningZoneId;
    
    // Get the new belt image
    const newBeltImage = getBeltImage(currentMiningZoneId);
    
    debugLog('Mining zone transition check', {
      currentMiningZoneId,
      newBeltImage,
      currentBelt,
      isSame: newBeltImage === currentBelt
    });
    
    // Only proceed with transition if the belt image actually changes
    if (newBeltImage !== currentBelt) {
      debugLog('Mining zone transition started', {
        prevBelt: currentBelt,
        newBelt: newBeltImage
      });
      
      // Set up the transition state
      setPrevBelt(currentBelt);
      setCurrentBelt(newBeltImage);
      setIsBeltTransitioning(true);
      
      // Reset transition flag after animation completes
      const timerRef = setTimeout(() => {
        setIsBeltTransitioning(false);
        debugLog('Mining zone transition ended');
      }, 800); // Slightly longer than CSS transition to ensure it completes
      
      // Clean up the timeout if the component unmounts during transition
      return () => clearTimeout(timerRef);
    }
  }, [currentMiningZoneId]);

  // Responsive: update container width on resize
  useEffect(() => {
    function handleResize() {
      if (shipsVisualRef.current) {
        setContainerWidth(shipsVisualRef.current.offsetWidth);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dynamically adjust asteroid position for fullscreen
  const ASTEROID_POS = isFullscreen ? 0.97 : 0.85;

  const computeWarpDurationMs = (ship: ShipDto) => {
    const remaining = ship.estimatedTimeToComplete ?? 0;
    const elapsed = ship.timeSinceStatusChange ?? 0;
    const totalSeconds = remaining + elapsed;
    const fallbackSeconds = remaining || 10;
    const seconds = totalSeconds > 0 ? totalSeconds : fallbackSeconds;
    return Math.max(200, seconds * 1000);
  };

  const getAnimationPlanForShip = (ship: ShipDto) => {
    if (ship.status === ShipStatus.WarpingToAsteroidBelt) {
      return {
        start: MOTHERSHIP_POS,
        target: ASTEROID_POS,
        duration: computeWarpDurationMs(ship)
      };
    }
    if (isShipWarping(ship)) {
      return {
        start: ASTEROID_POS,
        target: MOTHERSHIP_POS,
        duration: computeWarpDurationMs(ship)
      };
    }
    if (ship.location === ShipLocation.AsteroidBelt) {
      return {
        start: ASTEROID_POS,
        target: ASTEROID_POS,
        duration: 500
      };
    }
    return {
      start: MOTHERSHIP_POS,
      target: MOTHERSHIP_POS,
      duration: 500
    };
  };

  // Animate stars background
  useEffect(() => {
    const canvas = starsCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    // Star state
    if (!(canvas as any)._stars || (canvas as any)._fullscreen !== isFullscreen) {
      // Regenerate stars for fullscreen/small mode
      const starCount = isFullscreen ? 180 : 60;
      (canvas as any)._stars = Array.from({ length: starCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: isFullscreen ? Math.random() * 0.7 + 0.2 : Math.random() * 1.2 + 0.3,
        speed: Math.random() * 0.15 + 0.05,
        alpha: Math.random() * 0.5 + 0.5
      }));
      (canvas as any)._fullscreen = isFullscreen;
    }
    function drawStars() {
      ctx!.clearRect(0, 0, w, h);
      ctx!.save();
      ctx!.globalAlpha = 0.9;
      ctx!.fillStyle = '#0a0e17';
      ctx!.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--color-bg-primary').trim();
      ctx!.fillRect(0, 0, w, h);
      ctx!.restore();
      for (const star of (canvas as any)._stars) {
        // Draw stars across the entire canvas in all modes
        ctx!.save();
        ctx!.globalAlpha = star.alpha;
        ctx!.beginPath();
        ctx!.arc(star.x, star.y, star.r, 0, 2 * Math.PI);
        ctx!.fillStyle = '#fff';
        ctx!.shadowColor = '#fff';
        ctx!.fillStyle = 'var(--color-text-primary)';
        ctx!.shadowColor = 'var(--color-text-primary)';
        ctx!.shadowBlur = isFullscreen ? 2 : 6;
        ctx!.fill();
        ctx!.restore();
        star.x += star.speed;
        if (star.x > w) {
          star.x = 0;
          star.y = Math.random() * h;
          star.r = isFullscreen ? Math.random() * 0.7 + 0.2 : Math.random() * 1.2 + 0.3;
          star.speed = Math.random() * 0.15 + 0.05;
          star.alpha = Math.random() * 0.5 + 0.5;
        }
      }
      requestAnimationFrame(drawStars);
    }
    drawStars();
  }, [isFullscreen]);

  // Calculate responsive sizes for ships and images
  const baseWidth = Math.min(containerWidth, 1200);
  const containerHeight = isFullscreen ? 400 : 120; // taller in fullscreen
  const mothershipHeight = containerHeight * 0.75;
  const mothershipPx = Math.max(120, mothershipHeight * 2);
  const shipPx = Math.max(24, mothershipHeight * 0.3);
  // Asteroid belt size: restore original size for normal view, larger for fullscreen
  const asteroidPx = isFullscreen ? Math.max(320, baseWidth * 0.22) : Math.max(180, baseWidth * 0.2);
  const leftOffsetPx = (mothershipPx / 2) - (shipPx / 2);
  const rightOffsetPx = (asteroidPx / 2) - (shipPx / 2);
  const travelWidthPx = baseWidth - leftOffsetPx - rightOffsetPx;  // Store persistent ship animations in a ref so they survive re-renders
  const shipAnimationsRef = useRef<Record<string, ShipAnimationState>>({});
  // Store current ship keys in a ref so both effects can access it
  const currentShipKeysRef = useRef<Set<string>>(new Set());
  // Store last fleet update timestamp to detect page reloads vs regular data updates
  const lastUpdateRef = useRef<number>(Date.now());
  // Animation state is now handled in the combined animation and rendering loop// Update ship animation states based on fleet data
  useEffect(() => {
    if (!Array.isArray(fleet)) return;

    const now = Date.now();
    const animations = shipAnimationsRef.current;
    const currentShipKeys = new Set<string>();

    lastUpdateRef.current = now;    fleet.forEach((ship, idx) => {
      // Check both type and category to determine if we should include this ship
      const shipTypeStr = ship.type.toString();
      
      const shouldInclude = 
        shipTypeStr === ShipType.Mining.toString() ||
        shipTypeStr === ShipType.Hauler.toString() ||
        shipTypeStr === ShipType.Command.toString() ||
        shipTypeStr === ShipType.Freighter.toString() ||
        ship.category === ShipCategory.Mining ||
        ship.category === ShipCategory.Hauler ||
        ship.category === ShipCategory.Command;
        
      if (!shouldInclude) {
        // Skip ships that don't match our filter
        return;
      }

      const shipKey = ship.id || ship.name || idx;
      currentShipKeys.add(String(shipKey));

      let anim = animations[shipKey];
      const statusChanged = !anim || String(ship.status) !== anim.status;

      // --- Animation state logic for constant speed warp ---
      if (!anim || statusChanged) {
        const plan = getAnimationPlanForShip(ship);
        let startTime = now;
        let currentPos = plan.target;

        if (isShipWarping(ship)) {
          const elapsedMs = Math.min(plan.duration, Math.max(0, (ship.timeSinceStatusChange ?? 0) * 1000));
          const progress = plan.duration > 0 ? elapsedMs / plan.duration : 0;
          currentPos = plan.start + (plan.target - plan.start) * progress;
          startTime = now - elapsedMs;
        }

        if (!anim) {
          anim = animations[shipKey] = {
            pos: currentPos,
            targetPos: plan.target,
            startPos: plan.start,
            startTime,
            duration: Math.max(200, plan.duration),
            status: String(ship.status) || ''
          };
        }

        anim.startPos = plan.start;
        anim.targetPos = plan.target;
        anim.startTime = startTime;
        anim.duration = Math.max(200, plan.duration);
        anim.status = String(ship.status) || '';
        anim.pos = currentPos;
        // Store original duration and start time for constant speed
        // @ts-ignore
        anim.originalDuration = anim.duration;
        // @ts-ignore
        anim.originalStartTime = anim.startTime;
      }
      // Update animation timing for warping ships to stay synchronized with ETA
      else if (isShipWarping(ship) && ship.estimatedTimeToComplete) {
        // Recalculate animation timing to match current ETA
        // This ensures animation stays synchronized even if there are slight timing differences
        const currentETA = ship.estimatedTimeToComplete * 1000; // Convert to milliseconds
        const expectedEndTime = now + currentETA;
        
        // @ts-ignore
        if (anim.originalStartTime && anim.originalDuration) {
          // @ts-ignore
          const originalEndTime = anim.originalStartTime + anim.originalDuration;
          const timeDiff = Math.abs(expectedEndTime - originalEndTime);
          
          // If there's a significant difference (>1 second), re-sync the animation
          if (timeDiff > 1000) {
            // @ts-ignore
            anim.originalStartTime = expectedEndTime - currentETA;
            // @ts-ignore
            anim.originalDuration = currentETA;
          }
        }
      }
      // For non-warping ships, handle docked state
      else if (!isShipWarping(ship)) {
        // Docked
        let dockedPos = (ship.location === ShipLocation.Mothership) ? MOTHERSHIP_POS : ASTEROID_POS;
        anim.pos = dockedPos;
        anim.targetPos = dockedPos;
        anim.startPos = dockedPos;
        anim.startTime = now;
        anim.duration = 500;
        anim.status = String(ship.status) || '';
        // @ts-ignore
        anim.originalDuration = 500;
        // @ts-ignore
        anim.originalStartTime = now;
      }
    });

    currentShipKeysRef.current = currentShipKeys;

    Object.keys(animations).forEach(key => {
      if (!currentShipKeys.has(key)) {
        delete animations[key];
      }
    });
  }, [fleet]);  // Track ship elements to avoid recreating them on every render
  const shipElementsRef = useRef<Record<string, HTMLDivElement>>({});
  // Use a ref for the animation frame to prevent multiple animation loops
  const animationFrameRef = useRef<number | null>(null);

  // Combine both animation and rendering into a single loop for better performance
  useEffect(() => {
    const shipsVisual = shipsVisualRef.current;
    if (!shipsVisual) return;

    // Cancel any existing animation frame to prevent duplicate loops
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Cache dimensions to avoid recalculations in the animation loop
    const renderDimensions = {
      leftOffsetPx,
      travelWidthPx,
      shipPx
    };    // Create a stable reference for ship rendering info to reduce memory allocation
    const shipRenderInfo = new Map();
    
    function getShipImageBasedOnType(ship: ShipDto): string {
      // Convert ship type to string for safer comparison
      const shipTypeStr = ship.type.toString();
      
      if (shipTypeStr === ShipType.Hauler.toString()) {
        return haulerShipImg;
      } else if (shipTypeStr === ShipType.Mining.toString()) {
        return miningFrigateImg;
      } else if (shipTypeStr === ShipType.Command.toString()) {
        return commandShipImg;
      } else if (shipTypeStr === ShipType.Freighter.toString()) {
        return freighterImg;
      }else if (shipTypeStr === ShipType.Barge.toString()) {
        return bargeImg;
      }
      return miningFrigateImg;
    }
      function getShipScaleBasedOnType(ship: ShipDto): number {
      // Convert ship type to string for safer comparison
      const shipTypeStr = ship.type.toString();
      let scale = 1; // Default scale
      
      if (shipTypeStr === ShipType.Hauler.toString() ) {
        scale = 1.6; // Haulers are large
      } else if (shipTypeStr === ShipType.Mining.toString()) {
        scale = 0.75; // Mining ships are smaller
      } else if (shipTypeStr === ShipType.Command.toString()) {
        scale = 1; // Command ships are medium-large
      } else if (shipTypeStr === ShipType.Freighter.toString()) {
        scale = 3; // Freighters are largest
      }else if (shipTypeStr === ShipType.Barge.toString()) {
        scale = 1; // Barges are large
      }
      
      debugLog(`Ship scaling: ${ship.name || ship.id} (Type: ${shipTypeStr}) - Scale: ${scale}`);
      return scale;
    }

    function animateAndRenderShips() {
      const now = Date.now();
      const animations = shipAnimationsRef.current;
      const shipsVisualCurrent = shipsVisualRef.current;

      if (!shipsVisualCurrent) return;
      if (!Array.isArray(fleet) || fleet.length === 0) {
        return;
      }

      // First update all animation states
      Object.keys(animations).forEach(key => {
        const anim = animations[key];

        // Linear progress for animation: use originalStartTime/originalDuration if available
        // @ts-ignore
        const animStartTime = anim.originalStartTime !== undefined ? anim.originalStartTime : anim.startTime;
        // @ts-ignore
        const animDuration = anim.originalDuration !== undefined ? anim.originalDuration : anim.duration;
        const elapsed = now - animStartTime;
        const progress = Math.max(0, Math.min(1, animDuration > 0 ? elapsed / animDuration : 1));
        anim.pos = anim.startPos + (anim.targetPos - anim.startPos) * progress;

        if (progress >= 1) {
          anim.pos = anim.targetPos;
        }
      });      // Then render all ships - maintain presence of all ships in the DOM
      // Collect ships for vertical layout
      const miningShips: any[] = [];      fleet.forEach((ship, idx) => {
        // Check both type and category to determine if we should include this ship
        const shipTypeStr = ship.type.toString();
        
        const shouldInclude = 
          shipTypeStr === ShipType.Mining.toString() ||
          shipTypeStr === ShipType.Hauler.toString() ||
          shipTypeStr === ShipType.Command.toString() ||
          shipTypeStr === ShipType.Freighter.toString() ||
          shipTypeStr === ShipType.Barge.toString() ||
          ship.category === ShipCategory.Mining ||
          ship.category === ShipCategory.Hauler ||
          ship.category === ShipCategory.Command;
          
        if (!shouldInclude) return;
        miningShips.push({ ship, idx });
      });

      // Sort miningShips by a stable key so clump position doesn't change during animation
      miningShips.sort((a, b) => {
        const keyA = String(a.ship.id || a.ship.name || a.idx);
        const keyB = String(b.ship.id || b.ship.name || b.idx);
        return keyA.localeCompare(keyB);
      });

      const shipCount = miningShips.length;
      const availableHeight = containerHeight - shipPx; // leave some margin
      const maxShipsWithoutOverlap = Math.floor(availableHeight / shipPx) + 1;
      let verticalSpacing = 0;
      let startY = 0;

      if (shipCount <= maxShipsWithoutOverlap) {
        // Space ships evenly within available height
        verticalSpacing = shipCount > 1 ? availableHeight / (shipCount - 1) : 0;
        startY = 0;
      } else {
        // Compress spacing so all ships fit, but never overlap
        verticalSpacing = availableHeight / (shipCount - 1);
        startY = 0;
      }

      // Prepare rendering info before touching DOM
      miningShips.forEach(({ ship, idx }, miningIdx) => {
        const shipKey = ship.id || ship.name || idx;
        const anim = animations[shipKey];

        // Use animated position if available, otherwise fallback to static position
        let pos = 0.5;
        if (anim) {
          pos = anim.pos;
        } else if (ship.location === ShipLocation.Mothership) {
          pos = 0;
        } else if (ship.location === ShipLocation.AsteroidBelt) {
          pos = 1;
        }

        // Determine ship orientation and effects
        let faceRight = false;
        if (
          ship.status === ShipStatus.WarpingToAsteroidBelt ||
          ship.location === ShipLocation.AsteroidBelt ||
          ship.status === 'Mining'
        ) {
          faceRight = false;
        } else if (
          isShipWarping(ship) || ship.location === ShipLocation.Mothership || ship.status === 'OffloadingCargo'
        ) {
          faceRight = true;
        }

        let filter = '';
        if (isShipWarping(ship)) {
          filter = 'drop-shadow(0 0 8px #4da6ff)';
        }

        // Calculate vertical position: clump/group ships, never overlap
        const topPx = startY + miningIdx * verticalSpacing;

        // Add horizontal stagger: alternate left/right, or use a sine wave for more natural spread
        const staggerAmplitude = shipPx * 0.7;
        const staggerX = Math.sin(miningIdx) * staggerAmplitude;        // --- Ship size logic ---
        let visualShipPx = shipPx * getShipScaleBasedOnType(ship);

        // Debug info to help diagnose scaling issues
        debugLog('Ship Scaling', {
          shipId: ship.id,
          shipName: ship.name,
          shipType: ship.type,
          category: ship.category,
          status: ship.status,
          visualShipPx,
          calculatedScale: getShipScaleBasedOnType(ship),
          mothershipPx,
          shipPx,
          containerWidth,
          isFullscreen
        });

        // Adjust vertical position for large ships so they stay visually centered
        let adjustedTopPx = topPx;
        if (visualShipPx > shipPx) {
          adjustedTopPx = topPx - (visualShipPx - shipPx) / 2;
        }

        // Store all rendering info for this ship
        shipRenderInfo.set(shipKey, {
          pos,
          leftPx: renderDimensions.leftOffsetPx + pos * renderDimensions.travelWidthPx + staggerX,
          topPx: adjustedTopPx,
          title: `${ship.name || ship.id} (Type: ${ship.type})`,
          faceRight,
          filter,
          miningIdx,
          visualShipPx
        });
      });
      // Now update the DOM with minimal operations and hardware acceleration
      // First, ensure all ship elements still exist in DOM for consistent rendering
      fleet.forEach((ship, idx) => {          const shipKey = ship.id || ship.name || idx;
          if (!shipElementsRef.current[shipKey]) {
            const shipDiv = document.createElement('div');
            shipDiv.style.position = 'absolute';
            
            // Set initial size based on type using our helper function
            let visualShipPx = shipPx;
              // Debug info to help diagnose scaling issues
            const scale = getShipScaleBasedOnType(ship);
            const typeStr = ship.type.toString();
            
            // Set a data attribute for debugging
            shipDiv.dataset.shipType = typeStr;
            shipDiv.dataset.shipScale = scale.toString();
            
            shipDiv.style.width = `${visualShipPx}px`;
            shipDiv.style.height = `${visualShipPx}px`;
            shipDiv.style.willChange = 'transform';
            shipDiv.className = 'ship-element';
            // Create the image element once
            const img = document.createElement('img');
            img.src = getShipImageBasedOnType(ship);
            img.alt = getShipImageBasedOnType(ship);
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            img.style.willChange = 'transform, filter';
            shipDiv.appendChild(img);
            shipElementsRef.current[shipKey] = shipDiv;
            shipsVisualCurrent.appendChild(shipDiv);
          }

          const img = shipElementsRef.current[shipKey]?.querySelector('img') as HTMLImageElement;
          if (img) {
            if (isShipWarping(ship)){
              img.style.filter = 'drop-shadow(0 0 8px #4da6ff)';
            } else {
              img.style.filter = '';
            }
          }
      });

      // Now update all the ships with their latest positions
      shipRenderInfo.forEach((info, shipKey) => {
        const shipDiv = shipElementsRef.current[shipKey];
        if (!shipDiv) return;
        // Set the ship size based on type
        shipDiv.style.width = `${info.visualShipPx}px`;
        shipDiv.style.height = `${info.visualShipPx}px`;
        // Use hardware-accelerated transform for smoother animation
        // Combine all transforms into a single transform property for better performance
        shipDiv.style.transform = `translate3d(${info.leftPx.toFixed(3)}px, ${info.topPx}px, 0)`;
        shipDiv.style.left = '0';
        shipDiv.style.top = '0';
        shipDiv.title = info.title;

        // Update the image only where needed, avoid unnecessary DOM changes
        const img = shipDiv.querySelector('img') as HTMLImageElement;
        if (img) {
          if (img.dataset.facing !== String(info.faceRight)) {
            img.style.transform = `scaleX(${info.faceRight ? 1 : -1})`;
            img.dataset.facing = String(info.faceRight);
          }
          if (img.dataset.filter !== info.filter) {
            img.style.filter = info.filter;
            img.dataset.filter = info.filter;
          }

          // Add or update the warp trail glow effect
          let trail = shipDiv.querySelector('.warp-trail') as HTMLDivElement;
          // Determine direction of travel for trail placement
          let showTrail = false;
          let trailOnLeft = false;
          if (info.filter) {
            // Find animation state for this ship
            const anim = shipAnimationsRef.current[shipKey];
            if (anim && anim.startPos !== undefined && anim.targetPos !== undefined) {
              if (anim.targetPos > anim.startPos) {
                // Moving right, trail on left
                showTrail = true;
                trailOnLeft = true;
              } else if (anim.targetPos < anim.startPos) {
                // Moving left, trail on right
                showTrail = true;
                trailOnLeft = false;
              } else {
                // Not moving, fallback to facing direction
                showTrail = true;
                trailOnLeft = !info.faceRight;
              }
            } else {
              // Fallback if no anim info
              showTrail = !!info.filter;
              trailOnLeft = !info.faceRight;
            }
          }
          if (showTrail) {
            if (!trail) {
              trail = document.createElement('div');
              trail.className = 'warp-trail';
              trail.style.position = 'absolute';
              trail.style.top = '15%'; // Only cover the back half vertically
              trail.style.height = '70%';
              trail.style.pointerEvents = 'none';
              trail.style.zIndex = '0';
              shipDiv.insertBefore(trail, img); // behind the ship image
            }
            if (trailOnLeft) {
              // Trail on left (behind when moving right)
              trail.style.left = '-45%';
              trail.style.right = '';
              trail.style.width = '55%';
              trail.style.background = 'linear-gradient(90deg, var(--warp-glow-color) 0%, rgba(77,166,255,0.0) 100%)';
              trail.style.borderRadius = '60% 0 0 60%/60% 100% 100% 60%';
            } else {
              // Trail on right (behind when moving left)
              trail.style.left = '';
              trail.style.right = '-45%';
              trail.style.width = '55%';
              trail.style.background = 'linear-gradient(270deg, var(--warp-glow-color) 0%, rgba(77,166,255,0.0) 100%)';
              trail.style.borderRadius = '0 60% 60% 0/100% 60% 60% 100%';
            }
            trail.style.filter = 'blur(10px)';
            trail.style.opacity = '1';
            trail.style.transition = 'opacity var(--transition-speed-fast)';
          } else if (trail) {
            // Hide the trail if not warping
            trail.style.opacity = '0';
          }
        }
      });

      // Keep ships in the DOM even if they're not currently in the fleet data
      // This prevents flickering when data refreshes happen
      // Only remove ships that haven't been seen for a while
      const removalTime = 5000; // 5 seconds 
      Object.keys(shipElementsRef.current).forEach(key => {
        if (!currentShipKeysRef.current.has(key)) {
          // Instead of immediately removing, mark for potential removal
          const shipDiv = shipElementsRef.current[key];
          if (!shipDiv.dataset.lastSeen) {
            shipDiv.dataset.lastSeen = String(now);
          } else if (now - parseInt(shipDiv.dataset.lastSeen) > removalTime) {
            // Only remove if it's been missing for a while
            if (shipDiv.parentElement) {
              shipDiv.remove();
            }
            delete shipElementsRef.current[key];
          }
        } else {
          // Reset the last seen time for active ships
          shipElementsRef.current[key].dataset.lastSeen = '';
        }
      });
      // Continue animation loop with the ref to ensure only one is active
      animationFrameRef.current = requestAnimationFrame(animateAndRenderShips);
    }

    // Start the combined animation & rendering loop
    animationFrameRef.current = requestAnimationFrame(animateAndRenderShips);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [fleet, leftOffsetPx, travelWidthPx, shipPx]);

  // Optional: Use Fullscreen API for true fullscreen
  useEffect(() => {
    if (isFullscreen && fullscreenRef.current) {
      if (fullscreenRef.current.requestFullscreen) {
        fullscreenRef.current.requestFullscreen();
      }
    } else if (!isFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    }
    // Clean up on unmount
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    };
  }, [isFullscreen]);

  return (
    <div
      ref={fullscreenRef}
      id="fleet-visualization-bg"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: isFullscreen ? 'fixed' : undefined,
        top: isFullscreen ? 0 : undefined,
        left: isFullscreen ? 0 : undefined,
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : undefined,
        zIndex: isFullscreen ? 9999 : undefined,
        background: isFullscreen ? 'var(--color-bg-primary, #0a0e17)' : undefined,
        transition: 'all 0.3s',
      }}
    >
      {/* Expand/Collapse Button */}
      {/* <button
        onClick={() => setIsFullscreen(f => !f)}
        style={{
          position: 'absolute',
          top: 18,
          right: 28,
          zIndex: 10001,
          background: 'rgba(20,20,30,0.8)',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '8px 14px',
          fontSize: 18,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
        }}
        title={isFullscreen ? 'Exit Fullscreen' : 'Expand'}
      >
        {isFullscreen ? (
          <span style={{fontSize: 22, marginRight: 6}}>&#x2715;</span>
        ) : (
          <span style={{fontSize: 22, marginRight: 6}}>&#x26F6;</span>
        )}
        {isFullscreen ? 'Close' : 'Expand'}
      </button> */}
      <div id="fleet-visualization" style={{
        position: 'relative',
        minWidth: 0,
        minHeight: isFullscreen ? 400 : 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--color-bg-primary)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        overflow: 'hidden',
        width: isFullscreen ? '90vw' : '100%',
        height: isFullscreen ? '80vh' : undefined,
        maxWidth: isFullscreen ? 'none' : undefined,
        margin: isFullscreen ? 'auto' : undefined,
        transition: 'all 0.3s',
      }}>

        <canvas
          ref={starsCanvasRef}
          id="fleet-stars"
          width={isFullscreen ? 2400 : 1200}
          height={isFullscreen ? 400 : 120}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 'var(--z-index-base)'
          }}
        />
        <div id="mothership-visual" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 'var(--z-index-above)'
        }}>
          <img
            src={mothershipImg}
            alt="Mothership"
            style={{
              width: mothershipPx,
              height: mothershipHeight,
              borderRadius: '45px/90px',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              background: 'none'
            }}
          />
        </div>
        <div
          ref={shipsVisualRef}
          id="ships-visual"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            minWidth: 0,
            position: 'relative',
            height: containerHeight,
            flex: 1,
            zIndex: 'var(--z-index-above)'
          }}
        />
        <div id="belt-visual" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 'var(--z-index-above)',
          position: 'absolute',
          right: isFullscreen ? '2vw' : '-20px',
          bottom: 0,
          top: 0,
          height: '100%',
          width: asteroidPx * 0.85,
          justifyContent: isFullscreen ? 'center' : 'flex-end',
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          {/* Previous Belt (only during transitions) */}
          {isBeltTransitioning && prevBelt && (
            <img
              src={prevBelt}
              alt="Previous asteroid belt"
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'none',
                objectFit: isFullscreen ? 'contain' : 'cover',
                objectPosition: isFullscreen ? 'left' : 'unset',
                position: 'absolute',
                left: 0,
                top: 'auto',
                pointerEvents: 'none',
                borderBottomRightRadius: 'var(--radius-lg)',
                borderBottomLeftRadius: 'var(--radius-lg)',
                background: 'none',
                opacity: 0, // Start with opacity 0 and transition to it
                transition: 'opacity 0.6s ease-out', // Smooth fade-out
                zIndex: 2 // Layer above the new belt
              }}
              // Use ref callback for one-time initialization
              ref={(el) => {
                if (el && !el.dataset.initialized) {
                  // Mark as initialized to prevent repeated manipulation
                  el.dataset.initialized = 'true';
                  // Force a reflow to ensure the transition triggers
                  void el.offsetWidth;
                  // Then set initial opacity to 1 immediately
                  el.style.opacity = '1';
                  // After a small delay, start fading out
                  setTimeout(() => {
                    el.style.opacity = '0';
                  }, 50);
                }
              }}
            />
          )}
          
          {/* Current Belt (always visible) */}
          <img
            src={currentBelt}
            alt="Current asteroid belt"
            key={`current-belt-${currentBelt}`} // Ensure unique key for each belt image
            style={{
              width: '100%',
              height: '100%',
              maxWidth: 'none',
              objectFit: isFullscreen ? 'contain' : 'cover',
              objectPosition: isFullscreen ? 'left' : 'unset',
              position: 'absolute',
              left: 0,
              top: 'auto',
              pointerEvents: 'none',
              borderBottomRightRadius: 'var(--radius-lg)',
              borderBottomLeftRadius: 'var(--radius-lg)',
              background: 'none',
              opacity: isBeltTransitioning ? 0 : 1, // Hidden during transition start
              transition: 'opacity 0.6s ease-in',
              zIndex: 1 // Below the fading out image
            }}
            ref={(el) => {
              if (el && isBeltTransitioning && !el.dataset.initialized) {
                // Mark as initialized to prevent repeated manipulation
                el.dataset.initialized = 'true';
                // Force a reflow before setting opacity
                void el.offsetWidth;
                // Set to fully visible with a delay to ensure it shows after previous starts fading
                setTimeout(() => {
                  if (el) el.style.opacity = '1';
                }, 150);
              }
            }}
          />
          {showPirateAlert && <div className="pirate-flash-overlay" />}
          {pirateAttackActive && (
            <img
              src={pirateImg}
              alt="Pirate"
              className="pirate-ship"
              style={{
                position: 'absolute',
                bottom: isFullscreen ? '30%' : '40%',
                right: '10%',
                width: shipPx * 2,
                pointerEvents: 'none',
                zIndex: 3
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetVisualization;

// Helper function to check if a ship is warping
const isShipWarping = (ship: ShipDto): boolean => {
  return [
    ShipStatus.WarpingToMothership as number,
    ShipStatus.WarpingToMothershipForUpgrade as number,
    ShipStatus.WarpingToMothershipForOrders as number,
    ShipStatus.WarpingToMothershipForRepair as number,
    ShipStatus.WarpingToAsteroidBelt as number
  ].includes(ship.status);
};
